/**
 * Calcul de contraste WCAG 2.1.
 *
 * Implémenté ici plutôt qu'importé : la formule tient en quinze lignes, elle
 * est parfaitement spécifiée, et une dépendance de plus pour cela ne se
 * justifie pas.
 *
 * Référence : WCAG 2.1, §1.4.3 (Contrast Minimum) et §1.4.11 (Non-text
 * Contrast).
 */

/*
 * Seuils WCAG.
 *
 * Le seuil « grand texte » (3:1, ≥ 18,66 px gras ou ≥ 24 px) n'est
 * volontairement pas exposé : les palettes sont vérifiées à 4,5:1 pour tout le
 * texte, titres compris. Se prévaloir de l'exception affaiblirait les titres
 * sans rien simplifier.
 */
export const SEUIL_AA_TEXTE = 4.5;
/** Bordures, icônes, indicateurs d'état. */
export const SEUIL_AA_NON_TEXTE = 3;

/** `#rrggbb` → composantes 0-255. */
export function versRgb(hex: string): [number, number, number] {
  const propre = hex.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(propre)) {
    throw new Error(`Couleur hexadécimale attendue, reçu : ${hex}`);
  }
  return [
    parseInt(propre.slice(0, 2), 16),
    parseInt(propre.slice(2, 4), 16),
    parseInt(propre.slice(4, 6), 16),
  ];
}

/**
 * Luminance relative.
 *
 * La correction gamma (le seuil à 0,03928) n'est pas décorative : sans elle,
 * les couleurs sombres sortent avec un contraste largement surévalué, et des
 * combinaisons illisibles passeraient le test.
 */
export function luminance(hex: string): number {
  const [r, v, b] = versRgb(hex).map((composante) => {
    const c = composante / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * v + 0.0722 * b;
}

/** Rapport de contraste entre deux couleurs. De 1 (identiques) à 21. */
export function contraste(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const clair = Math.max(la, lb);
  const sombre = Math.min(la, lb);
  return (clair + 0.05) / (sombre + 0.05);
}

/** Arrondi lisible, pour les messages d'échec. */
export function contrasteArrondi(a: string, b: string): number {
  return Math.round(contraste(a, b) * 100) / 100;
}
